import {from, Subject} from 'rxjs';
import {finalize, mergeMap, takeUntil} from 'rxjs/operators';
import {Component, EventEmitter, forwardRef, Injector, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges} from '@angular/core';
import {AbstractControl, ControlValueAccessor, ValidationErrors, Validator, Validators, FormControl, FormGroup, NG_VALUE_ACCESSOR, NG_VALIDATORS} from '@angular/forms';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {AceLoggerService} from '@logging/ace-logger.service';
import {AceLogger} from '@logging/ace-logger';
import {Equipment, EquipmentId, Favourite, Metadata, MetadataId} from '@app/graphql-typings/graphql';
import {ReferenceLine} from '../reference-line';
import {TimePeriod} from '../../chart-functions/time-period';
import {FunctionName, FunctionParams} from '../../chart-functions/chart-function-types';
import {CopyEquipmentGroupComponent, EquipmentGroupData} from './copy-equipment-group/copy-equipment-group.component';
import {SelectFunctionModalComponent} from './select-function-modal/select-function-modal.component';
import {Task} from "@shared/services/api/task.interface";
import {SiteId} from '@shared/services/api/access-sites.interface';
import {EquipmentCache} from '@shared/data-stores/equipment-cache';
import {deferTask} from '@app/shared/util/angular/angular-utils';
import {FavouritesSelectComponent, FavouritesSelectSearchTerm} from '@shared/components/favourites-select/favourites-select.component';
import {AppConstants} from '@shared/util/app-constants';
import {IterableUtil} from '@shared/util/collections/collection-utils';
import {EquipmentSelectSearchTerm} from '@shared/components/equipment-single-select/equipment-select.component';
import {I18nMessage} from '@shared/api/model/i18n';

export interface FunctionParamsAndChartRawValues {
  functionParams: FunctionParams[];
  chartRawValues: boolean;
}

/**
 * Interface for the selected value of this control. That is, the writeValue method takes an object of this type, and the onChange method
 * provides the object of this type.
 */
export interface EquipmentGroup {
  equipment: Equipment;
  favourites: Favourite[];
  tasks: Task[];
  functionParamsAndChartRawValues?: FunctionParamsAndChartRawValues;
}

/**
 * Represents the options for each of the controls in a EquipmentGroupComponent.
 * Note that the favourites should only be not-empty if the component has a selected equipment.
 * That is, the EquipmentGroupComponent should be given an instance of EquipmentGroupOptions with non-empty
 * favourites only if this component has a selected equipment.
 */
export interface EquipmentGroupOptions {
  favourites: Favourite[];
}

/**
 * Used to retrieve the selected equipment all the controls of this type on the page.
 * Used to ensure the user does not re-select already selected equipment when copying an equipment group.
 */
export interface EquipmentRetriever {
  getEquipment(): Equipment[];
}

interface EquipmentGroupFormGroup {
  equipment: FormControl<Equipment>;
  favourites: FormControl<Favourite[]>;
  tasks: FormControl<Task[]>;
}

@Component({
  selector: 'app-equipment-group',
  templateUrl: './equipment-group.component.html',
  styleUrls: ['./equipment-group.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => EquipmentGroupComponent)
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => EquipmentGroupComponent),
      multi: true,
    }
  ],
  standalone: false
})
export class EquipmentGroupComponent implements OnInit, OnChanges, OnDestroy, ControlValueAccessor, Validator {

  @Input() referenceLines: ReferenceLine[];
  @Input() showTasksSelect = true;
  @Input() siteId: SiteId | null = null;
  @Input() options: EquipmentGroupOptions;
  @Input() equipmentRetriever: EquipmentRetriever;
  @Input() closeButton = true;
  @Input() isSingleUnitOnly = false;
  @Input() favouritesSearchTerm: FavouritesSelectSearchTerm | null = null;
  @Input() equipmentSearchTerm: EquipmentSelectSearchTerm | null = null;
  @Input() metadataConditionMetadata: Metadata | null = null;
  @Input() cloneDisabled = false;
  @Output() closeEvent: EventEmitter<any> = new EventEmitter();
  @Output() copyEvent: EventEmitter<{equipmentGroup: EquipmentGroup, options: EquipmentGroupOptions}[]> = new EventEmitter();
  @Output() cloning = new EventEmitter<boolean>();
  @Output() favouritesLoading = new EventEmitter<boolean>();
  @Output() favouritesLoaded: EventEmitter<{equipmentId: EquipmentId | null, favourites: Favourite[]}> = new EventEmitter();

  readonly CONTROL_NAME_EQUIPMENT = 'equipment';
  readonly CONTROL_NAME_FAVOURITES = 'favourites';
  readonly CONTROL_NAME_TASKS = 'tasks';
  readonly DROPDOWN_HOLDER_SELECTOR = AppConstants.DROPDOWN_HOLDER_SELECTOR;

  private readonly OPTIONS_NO_EVENT = {emitEvent: false};
  private readonly logger: AceLogger;
  private readonly ngUnsubscribe: Subject<void> = new Subject();

  form: FormGroup<EquipmentGroupFormGroup>;
  equipmentId: number;
  favouritesForChildren: Favourite[] | null;
  metadataConditionWarning: I18nMessage | null = null;
  cloneInProgress = false;

  private functionParamsAndChartRawValues: FunctionParamsAndChartRawValues;
  /** Checked to see if the condition metadata exists on the equipment so we can show a warning if not. null if favourites are still being fetched */
  private availableMetadataIds: Set<MetadataId> | null = null;

  onTouched: () => void = () => {};
  private onChange: (v: any) => void = (_) => {};
  private onValidatorChange: () => void = () => {};

  constructor(private modalService: NgbModal,
              private equipmentCache: EquipmentCache,
              private injector: Injector,
              logService: AceLoggerService) {
    this.logger = logService.create(EquipmentGroupComponent.name);
  }

  ngOnInit(): void {
    const initialEquipmentValue = null;
    this.form = new FormGroup({
      [this.CONTROL_NAME_EQUIPMENT]: new FormControl<Equipment>(initialEquipmentValue, Validators.required),
      [this.CONTROL_NAME_FAVOURITES]: new FormControl<Favourite[]>([], [Validators.required, this.sameUnitValidator]),
      [this.CONTROL_NAME_TASKS]: new FormControl<Task[]>([])
    });

    this.setDisabledStateForChildControls(initialEquipmentValue);

    this.form.valueChanges.pipe(takeUntil(this.ngUnsubscribe)).subscribe((v: EquipmentGroup) => {
      this.onEquipmentIdChange(v && v.equipment ? v.equipment.equipment_id : null);
      this.fireOnChange();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.options) {
      this.updateEquipmentReliantOptions();
    } else if (changes.metadataConditionMetadata) {
      this.updateMetadataConditionWarning();
    }
    if (changes.isSingleUnitOnly && this.form) {
      const favouritesControl = this.form.controls.favourites;
      const isFavouritesValid = favouritesControl.valid;
      // Rerun validation. `onlySelf: true` stops notifying form group. It does not stop emits so favourites control `statusChanges` will emit which `app-errors` rely on.
      favouritesControl.updateValueAndValidity({onlySelf: true});
      if (isFavouritesValid !== favouritesControl.valid) {
        if (!favouritesControl.valid) {
          favouritesControl.markAsTouched();
        }
        this.form.updateValueAndValidity(this.OPTIONS_NO_EVENT);
        deferTask(() => this.onValidatorChange());
      }
    }
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (this.form) {
      if (isDisabled) {
        this.form.get(this.CONTROL_NAME_EQUIPMENT).disable(this.OPTIONS_NO_EVENT);
        this.form.get(this.CONTROL_NAME_FAVOURITES).disable(this.OPTIONS_NO_EVENT);
        this.form.get(this.CONTROL_NAME_TASKS).disable(this.OPTIONS_NO_EVENT);
      } else {
        this.form.get(this.CONTROL_NAME_EQUIPMENT).enable(this.OPTIONS_NO_EVENT);
        this.setDisabledStateForChildControls(this.equipmentId);
      }
    }
  }

  writeValue(value: EquipmentGroup): void {
    if (value) {
      const previousEquipmentId = this.equipmentId;
      this.equipmentId = value.equipment ? value.equipment.equipment_id : null;
      if (this.equipmentId !== previousEquipmentId) {
        this.updateEquipmentReliantOptions();
      }
      this.form.get(this.CONTROL_NAME_EQUIPMENT).setValue(value.equipment, this.OPTIONS_NO_EVENT);
      this.form.get(this.CONTROL_NAME_FAVOURITES).setValue(value.favourites, this.OPTIONS_NO_EVENT);
      this.form.get(this.CONTROL_NAME_TASKS).setValue(value.tasks, this.OPTIONS_NO_EVENT);
      this.functionParamsAndChartRawValues = value.functionParamsAndChartRawValues;
    } else {
      this.equipmentId = null;
      this.updateEquipmentReliantOptions();
      this.form.get(this.CONTROL_NAME_EQUIPMENT).setValue(null, this.OPTIONS_NO_EVENT);
      this.form.get(this.CONTROL_NAME_FAVOURITES).setValue([], this.OPTIONS_NO_EVENT);
      this.form.get(this.CONTROL_NAME_TASKS).setValue([], this.OPTIONS_NO_EVENT);
      this.functionParamsAndChartRawValues = null;
    }
    this.setDisabledStateForChildControls(this.equipmentId);
    // Already validated but runs `updateValueAndValidity` again with `onlySelf: true` so that `statusChanges` will emit for `app-errors`
    this.form.controls.favourites.updateValueAndValidity({onlySelf: true});
  }

  validate(control: AbstractControl): ValidationErrors {
    return this.form.invalid ? {invalid: true} : null;
  }

  registerOnValidatorChange?(fn: () => void): void {
    this.onValidatorChange = fn;
  }

  close() {
    this.closeEvent.emit();
  }

  onFavouritesLoaded(favourites: Favourite[]): void {
    this.setAvailableMetadataIds(favourites);
    this.favouritesLoaded.emit({equipmentId: this.equipmentId, favourites: favourites});
  }

  copy(): void {
    if (this.siteId == null || this.cloneInProgress || this.isCloneDisabled()) {
      return;
    }
    this.cloneInProgress = true;
    this.cloning.emit(true);
    // Read the selection now, in case the form changes while the equipment is fetched
    const allSelectedEquipmentIds: Set<number> = new Set(this.getAllSelectedEquipment().map(e => e.equipment_id));
    const selectedEquipment: Equipment = this.form.get('equipment').value;
    const selectedFavourites: Favourite[] = this.form.get('favourites').value;
    const selectedTasks: Task[] = this.form.get('tasks').value;
    this.equipmentCache.getEquipmentForSite(this.siteId).pipe(
      mergeMap(siteEquipment => {
        // Do not offer equipment that is already selected (in the Equipment Group controls) as possible options
        const availableEquipment = siteEquipment.filter(e => {
          return e.metadata_type_id === selectedEquipment.metadata_type_id && !allSelectedEquipmentIds.has(e.equipment_id);
        });
        this.logger.debug('Available Equipment', availableEquipment);

        const modalRef = this.modalService.open(
          CopyEquipmentGroupComponent,
          {size: 'lg', centered: true, backdrop: 'static', keyboard: true, injector: this.injector}
        );
        modalRef.componentInstance.equipment = availableEquipment;
        modalRef.componentInstance.selectedEquipment = selectedEquipment;
        modalRef.componentInstance.selectedFavourites = selectedFavourites;
        modalRef.componentInstance.selectedTasks = selectedTasks;
        const modalSettled: Promise<EquipmentGroupData[] | null> = modalRef.result.catch((): null => null);
        const modalSettled$ = from(modalSettled);
        // Close the modal when this component is destroyed.
        this.ngUnsubscribe.pipe(takeUntil(modalSettled$)).subscribe(() => modalRef.dismiss());
        return modalSettled$;
      }),
      takeUntil(this.ngUnsubscribe),
      finalize(() => {
        this.cloneInProgress = false;
        this.cloning.emit(false);
      })
    ).subscribe(data => {
      if (data && data.length) {
        let fpacv: FunctionParamsAndChartRawValues = null;
        if (this.functionParamsAndChartRawValues) {
          fpacv = {
            functionParams: this.functionParamsAndChartRawValues.functionParams ? this.functionParamsAndChartRawValues.functionParams.map(f => {
              return {functionName: f.functionName, timePeriod: f.timePeriod ? new TimePeriod(f.timePeriod.getPeriod(), f.timePeriod.getAmount()) : null};
            }) : [],
            chartRawValues: this.functionParamsAndChartRawValues.chartRawValues
          };
        }

        this.copyEvent.emit(data.map(d => {
          return {
            equipmentGroup: {
              equipment: d.equipment,
              favourites: d.favourites,
              tasks: d.tasks,
              functionParamsAndChartRawValues: fpacv
            },
            options: {
              favourites: d.options.favourites
            }
          };
        }));
      }
    });
  }

  selectFunction(_event: MouseEvent) {
    this.onTouched();
    const modalRef = this.modalService.open(SelectFunctionModalComponent, {size: 'sm', centered: true, backdrop: 'static', keyboard: true});
    modalRef.componentInstance.functionParamsAndChartRawValues = this.functionParamsAndChartRawValues;
    modalRef.componentInstance.referenceLines = this.referenceLines;
    const modalSettled: Promise<FunctionParamsAndChartRawValues | null> = modalRef.result.catch((): null => null);
    const modalSettled$ = from(modalSettled);
    // Close the modal when this component is destroyed.
    this.ngUnsubscribe.pipe(takeUntil(modalSettled$)).subscribe(() => modalRef.dismiss());
    modalSettled$.subscribe(result => {
      if (result != null) {
        this.functionParamsAndChartRawValues = result;
        this.fireOnChange();
      }
    });
  }

  isSelectFunctionDisabled(): boolean {
    return !this.favouritesSelected();
  }

  isCloneDisabled(): boolean {
    return !this.favouritesSelected() || this.cloneDisabled;
  }

  getFunctionTextForIcon(): string {
    return this.functionParamsAndChartRawValues &&
      this.functionParamsAndChartRawValues.functionParams &&
      this.functionParamsAndChartRawValues.functionParams.length > 0 &&
      this.functionParamsAndChartRawValues.functionParams[0].functionName &&
      this.functionParamsAndChartRawValues.functionParams[0].functionName !== FunctionName.NONE ? this.functionParamsAndChartRawValues.functionParams[0].functionName : '';
  }

  onFavouritesLoadingChange(isLoading: boolean): void {
    this.favouritesLoading.emit(isLoading);
  }

  private readonly sameUnitValidator = (control: AbstractControl): ValidationErrors | null => {
    if (this.isSingleUnitOnly) {
      const favourites: Favourite[] = control.value ?? [];
      if (favourites.length > 1 && FavouritesSelectComponent.getSharedUnitId(favourites) == null) {
        return {mixedUnit: {messageKey: 'Favourites must share the same unit'}};
      }
    }
    return null;
  };

  private updateEquipmentReliantOptions(): void {
    this.favouritesForChildren = this.equipmentId != null ? this.options?.favourites ?? null : null;
    this.setAvailableMetadataIds(this.favouritesForChildren);
  }

  private setAvailableMetadataIds(favourites: Favourite[] | null): void {
    this.availableMetadataIds = favourites != null ? new Set(IterableUtil.keep(favourites, favourite => favourite.metadata_id)) : null;
    this.updateMetadataConditionWarning();
  }

  private updateMetadataConditionWarning(): void {
    const metadataId = this.metadataConditionMetadata?.metadata_id ?? null;
    this.metadataConditionWarning = metadataId != null && this.availableMetadataIds != null && !this.availableMetadataIds.has(metadataId)
      ? {messageKey: 'Equipment has no Metadata Condition Metadata', parameters: {metadata: this.metadataConditionMetadata.name}}
      : null;
  }

  private favouritesSelected(): boolean {
    const control = this.form.get(this.CONTROL_NAME_FAVOURITES);
    return control.enabled && control.value.length > 0;
  }

  private fireOnChange() {
    const value: EquipmentGroup = this.form.getRawValue();
    value.functionParamsAndChartRawValues = this.functionParamsAndChartRawValues;
    this.onChange(value);
  }

  private getAllSelectedEquipment(): Equipment[] {
    if (this.equipmentRetriever) {
      const equipment = this.equipmentRetriever.getEquipment();
      return equipment ? equipment : [];
    } else {
      return [];
    }
  }

  private onEquipmentIdChange(newEquipmentId: number): void {
    if (this.equipmentId !== newEquipmentId) {
      this.equipmentId = newEquipmentId ?? null;
      this.favouritesForChildren = null;
      this.setAvailableMetadataIds(null);
      const favouritesControl = this.form.get(this.CONTROL_NAME_FAVOURITES);
      favouritesControl.setValue([], this.OPTIONS_NO_EVENT);
      favouritesControl.markAsPristine();
      favouritesControl.markAsUntouched();

      const tasksControl = this.form.get(this.CONTROL_NAME_TASKS);
      tasksControl.setValue([], this.OPTIONS_NO_EVENT);
      tasksControl.markAsPristine();
      tasksControl.markAsUntouched();

      this.setDisabledStateForChildControls(newEquipmentId);
    }
  }

  private setDisabledStateForChildControls(equipmentId: number): void {
    if (equipmentId != null) {
      this.form.get(this.CONTROL_NAME_FAVOURITES).enable(this.OPTIONS_NO_EVENT);
      this.form.get(this.CONTROL_NAME_TASKS).enable(this.OPTIONS_NO_EVENT);
    } else {
      this.form.get(this.CONTROL_NAME_FAVOURITES).disable(this.OPTIONS_NO_EVENT);
      this.form.get(this.CONTROL_NAME_TASKS).disable(this.OPTIONS_NO_EVENT);
    }
  }
}
